import { useQuery } from '@tanstack/react-query';
import type { BillingStatusResponse } from '@hector/shared-types';
import { apiClient } from '@/lib/apiClient';
import { useCabinetId } from '@/hooks/useCabinetMemberMe';
import { ErrorService } from '@/lib/errors/ErrorService';

// Re-export so banner/settings consumers import the contract from the hook
// without reaching into @hector/shared-types themselves.
export type { BillingStatusResponse } from '@hector/shared-types';

export const BILLING_STATUS_KEY = 'billing-status';

/**
 * Billing status of the current cabinet — single source for the BillingBanner
 * and the Settings → Facturation tab.
 *
 * Reads `GET /api/stripe/status` (DB-cache only, zero Stripe API calls
 * server-side, works even when Stripe env is absent).
 *
 * Fail-open contract (same philosophy as `useManualPaywall`): any fetch error
 * resolves to `null` instead of an error state, so a backend hiccup can never
 * make a billing warning pop up (or block a paying customer). Consumers must
 * treat `null`/`undefined` data as "render nothing".
 */
export function useBillingStatus() {
  const cabinetId = useCabinetId();

  return useQuery<BillingStatusResponse | null>({
    queryKey: [BILLING_STATUS_KEY, cabinetId],
    queryFn: async (): Promise<BillingStatusResponse | null> => {
      try {
        return await apiClient.get<BillingStatusResponse>('/stripe/status');
      } catch (error) {
        // Fail-open: swallow (but log) the error so the query "succeeds" with
        // null and no banner renders — never surface billing noise on outages.
        // cabinet_id is auto-attached by PostHog (posthog.register) — not passed here.
        ErrorService.handle(error, { context: { action: 'fetch_billing_status' }, silent: true });
        return null;
      }
    },
    enabled: !!cabinetId,
    staleTime: 5 * 60 * 1000,
  });
}
